import { TONE, getToneFromScore } from './constants'

export const AUTHZ_INVITES_FROM_ADMINS_AND_GUEST_INVITERS = 'adminsAndGuestInviters'
export const RESTRICTED_GUEST_USER_ROLE_ID = '2af84b1e-32c8-42b7-82bc-daa82404023b'
export const USER_GUEST_USER_ROLE_ID = 'a0b1b346-4d3e-4e8b-98f8-753987be4970'
export const STANDARD_GUEST_USER_ROLE_ID = '10dae51f-b6af-4016-8d66-8c2a99b929b3'

const RECOMMENDED_INVITE_SETTINGS = new Set(['adminsandguestinviters', 'none'])
const USER_SELF_CONSENT_POLICY_PREFIX = 'managepermissiongrantsforself.'
const OWNED_RESOURCE_CONSENT_POLICY_PREFIX = 'managepermissiongrantsforownedresource.'
const RECOMMENDED_USER_SELF_CONSENT_POLICY_IDS = new Set([
  'microsoft-user-default-low',
  'microsoft-user-default-recommended',
])
const DEFAULT_USER_PERMISSION_GRANT_POLICY_RECOMMENDATION =
  'Self-consent disabled, Microsoft recommended, or low-risk only'

export const PERMISSION_GRANT_POLICY_ASSIGNMENT_TYPES = {
  USER_SELF_CONSENT: 'userSelfConsent',
  OWNED_RESOURCE_CONSENT: 'ownedResourceConsent',
  UNKNOWN: 'unknown',
}

// https://learn.microsoft.com/en-us/entra/identity/enterprise-apps/manage-app-consent-policies?pivots=ms-graph#app-consent-policy-segments
export const PERMISSION_GRANT_POLICY_LABEL_BY_ID = {
  'microsoft-user-default-low': 'Low-risk user consent',
  'microsoft-user-default-recommended': 'Microsoft managed recommendation',
  'microsoft-user-default-allow-consent-apps': 'Popular mail client consent',
  'microsoft-user-default-legacy': 'Legacy broad user consent',
  'microsoft-all-application-permissions': 'All application permissions',
  'microsoft-dynamically-managed-permissions-for-chat': 'Chat dynamic permissions',
  'microsoft-all-application-permissions-for-chat': 'All chat permissions',
  'microsoft-dynamically-managed-permissions-for-team': 'Team dynamic permissions',
  'microsoft-pre-approval-apps-for-chat': 'Chat pre-approved apps',
  'microsoft-pre-approval-apps-for-team': 'Team pre-approved apps',
  'microsoft-all-application-permissions-verified': 'All permissions for verified apps',
  'microsoft-application-admin': 'Application administrator consent',
  'microsoft-company-admin': 'Company administrator consent',
}

export const isPresent = (value) => value !== undefined && value !== null

export const isRecommendedInviteSetting = (value) => RECOMMENDED_INVITE_SETTINGS.has(String(value ?? '').toLowerCase())

export const getPermissionGrantPolicyAssignments = (value) => {
  if (!Array.isArray(value)) return []

  return value.map((rawPolicy) => {
    const raw = String(rawPolicy)
    const normalized = raw.toLowerCase()
    const getAssignment = (type, prefix) => {
      const policyId = normalized.replace(prefix, '')

      return {
        raw,
        type,
        policyId,
        label: PERMISSION_GRANT_POLICY_LABEL_BY_ID[policyId] ?? policyId,
        recommended: RECOMMENDED_USER_SELF_CONSENT_POLICY_IDS.has(policyId),
      }
    }

    if (normalized.startsWith(USER_SELF_CONSENT_POLICY_PREFIX)) {
      return getAssignment(PERMISSION_GRANT_POLICY_ASSIGNMENT_TYPES.USER_SELF_CONSENT, USER_SELF_CONSENT_POLICY_PREFIX)
    }

    if (normalized.startsWith(OWNED_RESOURCE_CONSENT_POLICY_PREFIX)) {
      return getAssignment(
        PERMISSION_GRANT_POLICY_ASSIGNMENT_TYPES.OWNED_RESOURCE_CONSENT,
        OWNED_RESOURCE_CONSENT_POLICY_PREFIX,
      )
    }

    return {
      raw,
      type: PERMISSION_GRANT_POLICY_ASSIGNMENT_TYPES.UNKNOWN,
      policyId: normalized,
      label: PERMISSION_GRANT_POLICY_LABEL_BY_ID[normalized] ?? raw,
      recommended: false,
    }
  })
}

const getUserSelfConsentPolicyIds = (policies) =>
  getPermissionGrantPolicyAssignments(policies)
    .filter((policy) => policy.type === PERMISSION_GRANT_POLICY_ASSIGNMENT_TYPES.USER_SELF_CONSENT)
    .map((policy) => policy.policyId)

export const isRecommendedPermissionGrantPoliciesAssigned = (value) => {
  if (!Array.isArray(value)) return false

  const userSelfConsentPolicyIds = getUserSelfConsentPolicyIds(value)
  if (userSelfConsentPolicyIds.length === 0) return true

  return userSelfConsentPolicyIds.every((policyId) => RECOMMENDED_USER_SELF_CONSENT_POLICY_IDS.has(policyId))
}

export const buildTenantAuthzRows = (tenant) => {
  const authorizationPolicy = tenant?.authorizationPolicy
  const permissions = authorizationPolicy?.defaultUserRolePermissions ?? {}

  return [
    {
      key: 'invites',
      setting: 'allowInvitesFrom',
      current: authorizationPolicy?.allowInvitesFrom,
      recommended: AUTHZ_INVITES_FROM_ADMINS_AND_GUEST_INVITERS,
      deviationTone: TONE.DANGER,
      tooltipId: 'AI_RISK_REPORT.TOOLTIP.AUTHZ.ALLOW_INVITES_FROM',
      isRecommended: isRecommendedInviteSetting,
    },
    {
      key: 'emailVerifiedJoin',
      setting: 'allowEmailVerifiedUsersToJoinOrganization',
      current: authorizationPolicy?.allowEmailVerifiedUsersToJoinOrganization,
      recommended: false,
      deviationTone: TONE.DANGER,
      tooltipId: 'AI_RISK_REPORT.TOOLTIP.AUTHZ.ALLOW_EMAIL_VERIFIED_USERS_TO_JOIN',
    },
    {
      key: 'emailBasedSubscriptions',
      setting: 'allowedToSignUpEmailBasedSubscriptions',
      current: authorizationPolicy?.allowedToSignUpEmailBasedSubscriptions,
      recommended: false,
      deviationTone: TONE.DANGER,
      tooltipId: 'AI_RISK_REPORT.TOOLTIP.AUTHZ.ALLOWED_TO_SIGN_UP_EMAIL_BASED_SUBSCRIPTIONS',
    },
    {
      key: 'riskyAppsConsent',
      setting: 'allowUserConsentForRiskyApps',
      current: authorizationPolicy?.allowUserConsentForRiskyApps,
      recommended: false,
      deviationTone: TONE.DANGER,
      tooltipId: 'AI_RISK_REPORT.TOOLTIP.AUTHZ.ALLOW_USER_CONSENT_FOR_RISKY_APPS',
    },
    {
      key: 'apps',
      setting: 'defaultUserRolePermissions.allowedToCreateApps',
      current: permissions.allowedToCreateApps,
      recommended: false,
      deviationTone: TONE.WARN,
      tooltipId: 'AI_RISK_REPORT.TOOLTIP.AUTHZ.ALLOWED_TO_CREATE_APPS',
    },
    {
      key: 'tenants',
      setting: 'defaultUserRolePermissions.allowedToCreateTenants',
      current: permissions.allowedToCreateTenants,
      recommended: false,
      deviationTone: TONE.DANGER,
      tooltipId: 'AI_RISK_REPORT.TOOLTIP.AUTHZ.ALLOWED_TO_CREATE_TENANTS',
    },
    {
      key: 'groups',
      setting: 'defaultUserRolePermissions.allowedToCreateSecurityGroups',
      current: permissions.allowedToCreateSecurityGroups,
      recommended: false,
      deviationTone: TONE.WARN,
      tooltipId: 'AI_RISK_REPORT.TOOLTIP.AUTHZ.ALLOWED_TO_CREATE_SECURITY_GROUPS',
    },
    {
      key: 'guestRole',
      setting: 'guestUserRoleId',
      current: authorizationPolicy?.guestUserRoleId,
      recommended: `${RESTRICTED_GUEST_USER_ROLE_ID} (RestrictedGuest)`,
      deviationTone: TONE.DANGER,
      tooltipId: 'AI_RISK_REPORT.TOOLTIP.AUTHZ.GUEST_USER_ROLE_ID',
      isRecommended: (value) => String(value ?? '').toLowerCase() === RESTRICTED_GUEST_USER_ROLE_ID,
    },
    {
      key: 'permissionGrantPolicy',
      setting: 'permissionGrantPolicy (default user)',
      current: permissions.permissionGrantPoliciesAssigned,
      recommended: DEFAULT_USER_PERMISSION_GRANT_POLICY_RECOMMENDATION,
      deviationTone: TONE.WARN,
      tooltipId: 'AI_RISK_REPORT.TOOLTIP.AUTHZ.PERMISSION_GRANT_POLICY',
      isRecommended: isRecommendedPermissionGrantPoliciesAssigned,
    },
  ].map((row) => {
    const hasCurrentValue = isPresent(row.current)
    const isRecommendedValue = row.isRecommended ? row.isRecommended(row.current) : row.current === row.recommended

    return {
      ...row,
      deviates: hasCurrentValue && !isRecommendedValue,
    }
  })
}

export const getTenantAuthzScore = (tenant) => {
  if (!tenant?.authorizationPolicy) return null

  const rows = buildTenantAuthzRows(tenant)
  const deviationCount = rows.filter((row) => row.deviates).length
  return Math.max(0, 100 - Math.round((deviationCount / rows.length) * 100))
}

export const getTenantAuthzTone = (tenant) => getToneFromScore(getTenantAuthzScore(tenant))
